import {h, Fragment} from "preact"
import { useResponse, AutocompleteElement, Keywords, useCurrencyFormatting, useAppStateSelector } from "@nosto/preact"
import { productImagePlaceholder, getSrcset, handleSubmitSearch } from "../helpers"
import { DROPDOWN_NORESULTS_RECID } from "../config"
import { useEffect, useRef } from "preact/hooks"

export default function Autocomplete() {
    const query = useAppStateSelector(state => state.query)
    const { products, keywords } = useResponse()
    const { format } = useCurrencyFormatting()
    const recRef = useRef(null)
    const hasResults = products?.hits?.length > 0 || keywords?.hits?.length > 0;
    
    useEffect(() => {
        if(typeof window.nostojs === 'function' && recRef.current?.children?.length === 0 && !hasResults) {
            window.nostojs(api => {
                api
                .createRecommendationRequest({ includeTagging: true })
                .addElements([ DROPDOWN_NORESULTS_RECID ])
                .load({ skipPageViews: true, skipEvents: true })
            })
        }
    }, [ recRef, hasResults ])

    return (
        <div
            class="ns-autocomplete group overflow-y-auto group-open/results:block w-full bg-t-background"
            data-nosto-element="autocomplete"
        >
            {/* results */}
            <div class={`flex flex-col lg:group-[.is-desktop]/results:grid lg:group-[.is-desktop]/results:grid-cols-[200px_1fr] group-[.is-mobile]/results:gap-md lg:group-[.is-desktop]/results:gap-md ${hasResults ? '' : 'ns-content-hidden'}`}>
                <div>
                    {keywords?.hits?.length > 0 && (
                        <>
                            <h2 
                                id="nosto-search-terms"
                                class="utility font-bold text-t-brand-secondary-contrast mb-sm lg:mb-md group-[.is-mobile]/results:px-sm"
                            >
                                Search Terms
                            </h2>
                            <ul 
                                class="flex scrollbar-none flex-row scroll-smooth snap-mandatory group-[.is-mobile]/results:px-sm group-[.is-mobile]/results:flex-nowrap group-[.is-mobile]/results:overflow-x-scroll group-[.is-desktop]/results:flex-col group-[.is-desktop]/results:overflow-x-hidden"
                                role="group"
                                aria-labelledby="nosto-search-terms"
                            >
                                <Keywords
                                    hits={keywords.hits}
                                    keywordComponent={Keyword}
                                    elementComponent={Element}
                                    keywordInCategoryComponent={KeywordInCategory}
                                />
                            </ul>
                        </>
                    )}
                </div>
                {products?.hits?.length > 0 && (
                    <>
                    <div class="flex flex-col gap-md group-[.is-mobile]/results:px-sm">
                        <div>
                            <div class="flex justify-between mb-xs items-start">
                                <h2 
                                    id="nosto-search-products"
                                    class="utility font-bold text-t-brand-secondary-contrast"
                                >
                                    Search Results for "{query.query}"
                                </h2>
                                <div class="lg:inline-flex hidden">
                                    <button 
                                        class="ns-submit text-t-link utility underline underline-offset-2 inline-flex items-center gap-2xs" 
                                        type="button"
                                        onClick={handleSubmitSearch}
                                    >
                                        <span>View all results for "{query.query}"</span>
                                        <span class="ns-submit-icon rotate-180 bg-t-brand-primary-contrast rounded-full w-5 h-5 flex justify-center items-center">
                                            <svg fill="none" class="icon icon-caret" id="icon-caret" viewBox="0 0 20 20" role="presentation" focusable="false" aria-hidden="true">
                                                <path stroke="currentColor" d="M14.167 19.167 6.146 10l8.02-9.167"></path>
                                            </svg>
                                        </span>
                                    </button>
                                </div>
                            </div>
                            <ul
                                class="grid items-start grid-cols-2 lg:group-[.is-desktop]/results:grid-cols-4 gap-xs"
                                aria-aria-labelledby="nosto-search-products"
                                role="group"
                            >
                                {products?.hits?.map(hit => (
                                    <AutocompleteElement 
                                        key={hit.productId} 
                                        hit={hit}
                                        as="li"
                                        role="option"
                                        class="cursor-pointer h-full col-span-1"
                                        aria-selected="false"
                                    >
                                        <component--product-card
                                            nosto-product-card
                                            product-id={ hit.productId } 
                                            show-reviews='false' 
                                            is-compact='true' 
                                            show-variant-picker='false' 
                                            show-customize-button='false'
                                            clickable-card='true'
                                            placeholder-aspect='0.80'
                                        >
                                        </component--product-card>
                                    </AutocompleteElement>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <div class="group-[.is-mobile]/results:px-sm lg:hidden">
                        <button 
                            class="ns-submit btn w-full" 
                            variant="primary" 
                            type="button"
                            onClick={handleSubmitSearch}
                        >
                            View all results for "{query.query}"
                        </button>
                    </div>
                    </>
                )}
            </div>

            {/* no results */}
            <div class={`flex flex-col ${hasResults ? 'ns-content-hidden' : ''}`}>
                <div>
                    <h3 class="text-center text-t-foreground mb-[30px] lg:mb-lg">
                        No results found for "{query.query}"
                    </h3>
                </div>
                <div 
                    class="nosto_element group-[.is-mobile]/results:px-sm" 
                    id={DROPDOWN_NORESULTS_RECID}
                    ref={recRef}
                />
            </div>
        </div>
    )
}

function Keyword({ hit }) {
    return (
        <div data-nosto-element="keyword">
            <p
                class="body-14 font-medium text-[#BABBC0] group-[.is-mobile]/results:border group-[.is-mobile]/results:border-t-ui-lines-2 group-[.is-mobile]/results:p-xs [&amp;>mark]:body-14 [&amp;>mark]:font-medium [&amp;>mark]:text-t-brand-secondary-contrast [&amp;>mark]:bg-t-background"
                aria-label={hit.keyword}
            >
                {hit?._highlight?.keyword ? (
                    <span
                        dangerouslySetInnerHTML={{
                            __html: hit._highlight.keyword
                        }}
                    ></span>
                ) : (
                    hit.keyword
                )}
            </p>
        </div>
    )
}

function Element({ hit, keywordComponent, filter }) {
    return (
        <AutocompleteElement 
            hit={hit} 
            filter={filter} 
            class="ns-keyword cursor-pointer aria-selected:bg-p-light shrink-0 py-2xs px-px"
            role="option"
            aria-selected="false"
        >
            {keywordComponent}
        </AutocompleteElement>
    )
}

function KeywordInCategory({ category, children }) {
    return (
        <span class="ns-clickable">
            {category?.value ? (
                <span>
                    {children}{" "}
                    <span class="ns-keyword-in-category">
                        in <span>{category.value}</span>
                    </span>
                </span>
            ) : (
                children
            )}
        </span>
    )
}
